import type {
  BouncePadPlacement,
  CollectiblePlacement,
  Gap,
  LevelData,
  ObstaclePlacement,
  ObstacleType,
  OpenEdge,
  RampPlacement,
  SurfaceZone,
  Vec3Lit,
} from './types';
import type { SurfaceId } from '../world/Surfaces';
import { LEVEL_1 } from './level1';

// Hand-tuned, distinct courses built from a parametrized generator that keeps
// all the guard rails (tapered descent + flat run-out, chasms only on straight
// sections with full bounce-pad rows, collectibles off chasms, etc.). Each
// config has a fixed seed so the course is consistent every time.
//
// Only three surfaces exist across every level: grass (default), ice (fast &
// slippery — the fast line) and mud (slow & sticky — the slow zone).

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Band {
  start: number;
  end: number;
}
const overlaps = (a: Band, b: Band, pad = 0): boolean =>
  a.start < b.end + pad && b.start < a.end + pad;

export interface ShelfConfig {
  side: 1 | -1;
  height: number;
  innerLN: number;
  tStart: number;
  tEnd?: number;
  forks: { tStart: number; tEnd: number }[];
}

export interface LevelConfig {
  id: string;
  name: string;
  seed: number;
  desc: number; // length (control points)
  ampA: number;
  freqA: number; // broad curves (low amp = straight)
  ampB: number;
  freqB: number; // wiggle
  baseHW: number; // track width
  branches: number; // alternate "tracks down" (widened sections)
  grippy: SurfaceId[];
  slippery: SurfaceId[];
  patchCount: [number, number];
  iceBias: number; // 0..1 chance a patch uses the slippery palette
  cliffCount: [number, number];
  rampCount: [number, number];
  obstacleCount: [number, number];
  obstacleTypes: ObstacleType[];
  obstacleSpread: number; // lateral spread (small = central hurdles)
  hurdleLine: boolean; // align the collectible line with the hurdles
  iceBase: boolean; // the whole track is ice (a few slower patches over it)
  laneLow: SurfaceId | null; // a slower "low" lane on one side
  moundForks: boolean; // widened sections get a tall central hill (L/R fork)
  shelf: ShelfConfig | null; // a raised "high lane" (the fast line) on one side
  endFlourish: boolean; // a little gauntlet (ramp + coins + rocks) near the finish
  gap: boolean;
  bridges: number; // narrow sections with drops on BOTH sides
  gradeBase: number;
  gradeSteep: number;
  // >0 makes the descent step down in alternating steep pitches and flatter
  // benches (a "mountain" face) instead of one uniform ramp; the number is how
  // many steep/flat cycles span the descent.
  benches: number;
  // 'snow' frosts the banks, mountainsides, trees and valley (see LevelData).
  theme?: 'green' | 'snow';
}

export function buildLevel(cfg: LevelConfig): LevelData {
  const rnd = mulberry32(cfg.seed);
  const rng = (lo: number, hi: number): number => lo + rnd() * (hi - lo);
  const irng = (lo: number, hi: number): number => Math.floor(rng(lo, hi + 1));
  const chance = (p: number): boolean => rnd() < p;
  const pick = <T>(arr: T[]): T => arr[Math.floor(rnd() * arr.length)];

  const DESC = cfg.desc;
  const PLATEAU = 4;
  const controlPoints: Vec3Lit[] = [];
  const halfWidths: number[] = [];
  const banks: number[] = [];

  const phaseA = rng(0, Math.PI * 2);
  const phaseB = rng(0, Math.PI * 2);
  // Mountain courses step down in alternating steep pitches and flatter benches
  // rather than one uniform ramp, so the eye gets a reference for how steep each
  // pitch is (a long even slope reads deceptively flat from above). A full-period
  // sine keeps the mean grade ~1, so net descent — and the overall pull of
  // gravity — is unchanged; it just redistributes where the drop happens. The
  // multiplier bottoms out at 0.2 (never inverts to uphill), so it's always
  // downhill, just steeper-then-gentler.
  const benchPhase = rng(0, Math.PI * 2);
  const benchAt = (p: number): number =>
    cfg.benches > 0 ? 1 + 0.8 * Math.sin(p * Math.PI * 2 * cfg.benches + benchPhase) : 1;
  const xOf = (p: number): number =>
    cfg.ampA * Math.sin(p * Math.PI * cfg.freqA + phaseA) +
    cfg.ampB * Math.sin(p * Math.PI * cfg.freqB + phaseB);

  // The "hurdle line": a gentle weave that both the hurdles and the dense
  // collectible line follow, so the rewarding line goes through the hurdles
  // while a clean line around them collects less.
  const hlPhase = rng(0, Math.PI * 2);
  const hlAt = (t: number): number => 0.5 * Math.sin(t * Math.PI * 2.4 + hlPhase);

  // Branch (widen) bands.
  const branches: Band[] = [];
  for (let k = 0; k < cfg.branches; k++) {
    const start = 0.3 + k * 0.3 + rng(-0.04, 0.04);
    const b: Band = { start, end: start + 0.12 };
    if (!branches.some((o) => overlaps(o, b, 0.04))) branches.push(b);
  }
  // Narrow bridge bands (drops on both sides), spread across the course. Keep
  // them out of the high-lane shelf segment (a raised one-sided lane and a
  // narrow drop-both-sides bridge can't share the same stretch).
  const shelfBand: Band | null = cfg.shelf
    ? { start: cfg.shelf.tStart - 0.03, end: (cfg.shelf.tEnd ?? 0.86) + 0.03 }
    : null;
  const bridgeBands: Band[] = [];
  for (let k = 0; k < cfg.bridges; k++) {
    const start = 0.18 + (k + 0.5) * (0.66 / Math.max(1, cfg.bridges)) + rng(-0.015, 0.015);
    const b: Band = { start, end: start + 0.04 };
    if (shelfBand && overlaps(shelfBand, b, 0)) continue;
    if (!bridgeBands.some((o) => overlaps(o, b, 0.04)) && !branches.some((o) => overlaps(o, b, 0.02))) {
      bridgeBands.push(b);
    }
  }

  const hwAt = (p: number): number => {
    if (p > 0.88) return cfg.baseHW + 1.5; // open finish straight
    if (bridgeBands.some((b) => p >= b.start && p <= b.end)) return 4.2; // narrow bridge
    if (branches.some((b) => p >= b.start && p <= b.end)) return cfg.baseHW + 4;
    if (cfg.shelf) return cfg.baseHW + 2; // room for the high & low lanes
    return cfg.baseHW;
  };

  let y = 96;
  let z = 0;
  let descentLen = 0;
  const dz = 30;
  for (let i = 0; i <= DESC; i++) {
    const p = i / DESC;
    controlPoints.push({ x: xOf(p), y, z });
    halfWidths.push(hwAt(p));
    if (i > 0) {
      const a = controlPoints[i - 1];
      const b = controlPoints[i];
      descentLen += Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    }
    const taper = p < 0.85 ? 1 : 1 - (p - 0.85) / 0.15;
    z -= dz;
    y -= dz * (cfg.gradeBase + p * cfg.gradeSteep) * taper * benchAt(p);
  }
  const last = controlPoints[DESC];
  const prev = controlPoints[DESC - 1];
  const ddx = last.x - prev.x;
  const ddz = last.z - prev.z;
  const len = Math.hypot(ddx, ddz) || 1;
  let px = last.x;
  let pz = last.z;
  for (let k = 1; k <= PLATEAU; k++) {
    px += (ddx / len) * dz;
    pz += (ddz / len) * dz;
    controlPoints.push({ x: px, y, z: pz });
    halfWidths.push(cfg.baseHW + 3);
  }
  const M = controlPoints.length - 1;
  for (let i = 0; i <= M; i++) {
    const a = controlPoints[Math.max(0, i - 1)];
    const b = controlPoints[i];
    const c = controlPoints[Math.min(M, i + 1)];
    const cross = (b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x);
    const mag = Math.hypot(b.x - a.x, b.z - a.z) * Math.hypot(c.x - b.x, c.z - b.z) || 1;
    banks.push(Math.max(-0.32, Math.min(0.32, (-cross / mag) * 0.9)));
  }

  // --- Open cliffs + bridges (both-side drops) ---------------------------
  const openEdges: OpenEdge[] = [];
  const cliffBands: Band[] = [];
  for (const b of bridgeBands) {
    openEdges.push({ side: 'both', tStart: b.start, tEnd: b.end });
    cliffBands.push(b);
  }

  // --- Base surfaces -----------------------------------------------------
  const gaps: Gap[] = [];
  const bouncePads: BouncePadPlacement[] = [];
  const surfaceZones: SurfaceZone[] = [];
  // Base surfaces span past the finish (tEnd > 1) so the run-out plateau —
  // which lives at playable t > 1 — stays ice instead of falling back to the
  // default grass, which made the finish run-out read green on a frozen course.
  if (cfg.iceBase) surfaceZones.push({ tStart: 0, tEnd: 2, surface: 'ice' });
  if (cfg.laneLow) {
    surfaceZones.push({ tStart: 0, tEnd: 2, lateralMin: -1, lateralMax: -0.25, surface: cfg.laneLow });
  }
  // The high lane and the low ground beside it are both ice (frozen the whole
  // way); the high lane is just the faster, more rewarding line.
  let gapBand: Band | null = null;
  let approachBand: Band | null = null;

  const curvatureAt = (t: number): number => {
    const idx = Math.max(1, Math.min(DESC - 1, Math.round(t * DESC)));
    const a = controlPoints[idx - 1];
    const b = controlPoints[idx];
    const c = controlPoints[idx + 1];
    const i1 = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    const o1 = Math.hypot(c.x - b.x, c.z - b.z) || 1;
    const dot = ((b.x - a.x) * (c.x - b.x) + (b.z - a.z) * (c.z - b.z)) / (i1 * o1);
    return 1 - Math.max(-1, Math.min(1, dot));
  };
  if (cfg.gap) {
    const windowed = (t: number): number => {
      let s = 0;
      let n = 0;
      for (let d = -0.04; d <= 0.041; d += 0.02) {
        s += curvatureAt(t + d);
        n++;
      }
      return s / n;
    };
    let gt = -1;
    let best = Infinity;
    for (let cand = 0.24; cand <= 0.66; cand += 0.01) {
      const onBridge = bridgeBands.some((b) => Math.abs((b.start + b.end) / 2 - cand) < 0.1);
      if (onBridge) continue;
      const cu = windowed(cand);
      if (cu < best) {
        best = cu;
        gt = cand;
      }
    }
    if (gt > 0 && best < 0.0055) {
      gaps.push({ tStart: gt, tEnd: gt + 0.006, exitT: gt + 0.02 });
      gapBand = { start: gt, end: gt + 0.006 };
      approachBand = { start: gt - 0.1, end: gt + 0.05 };
      // A fast ice run-up helps clear the chasm; the bounce-pad row does the rest.
      surfaceZones.push({ tStart: gt - 0.05, tEnd: gt - 0.004, surface: 'ice' });
      for (const lateral of [-1.0, -0.5, 0, 0.5, 1.0]) bouncePads.push({ t: gt - 0.005, lateral });
    }
  }
  const clearOfChasm = (b: Band, pad = 0): boolean => !approachBand || !overlaps(b, approachBand, pad);

  // --- Surface patches (ice on the bridges; themed elsewhere) ------------
  for (const b of bridgeBands) surfaceZones.push({ tStart: b.start, tEnd: b.end, surface: 'ice' });
  const maxPatches = irng(cfg.patchCount[0], cfg.patchCount[1]);
  const placedBands: Band[] = approachBand ? [approachBand] : [];
  for (let i = 0; i < maxPatches * 4 && placedBands.length < maxPatches + (approachBand ? 1 : 0); i++) {
    const start = rng(0.1, 0.84);
    const allowSlippery = clearOfChasm({ start, end: start }, 0.04) && cfg.slippery.length > 0;
    const isIce = allowSlippery && chance(cfg.iceBias);
    // Mud patches are short little blobs; ice can stretch out a bit longer.
    const band: Band = { start, end: start + (isIce ? rng(0.05, 0.09) : rng(0.03, 0.05)) };
    if (placedBands.some((bb) => overlaps(bb, band, 0.02))) continue;
    // Never lay mud across a branch/fork — those carry the side coin lines.
    if (!isIce && branches.some((br) => overlaps(br, band, 0.02))) continue;
    const surface = isIce ? pick(cfg.slippery) : pick(cfg.grippy);
    // Slow (mud) patches sit off to ONE side and never touch the centre, so a
    // clean central line always weaves around them — slowing down is a choice,
    // not a tax. Fast (ice) patches can span the whole width.
    let lateralMin: number | undefined;
    let lateralMax: number | undefined;
    if (surface === 'mud') {
      if (chance(0.5)) { lateralMin = 0.2; lateralMax = 1; }
      else { lateralMin = -1; lateralMax = -0.2; }
    }
    surfaceZones.push({ tStart: band.start, tEnd: band.end, surface, lateralMin, lateralMax });
    placedBands.push(band);
  }

  // --- Central-hill forks on the widened branch sections -----------------
  const mounds: { tStart: number; tEnd: number }[] = [];
  if (cfg.moundForks) {
    for (const branch of branches) {
      if (!clearOfChasm(branch, 0.03)) continue;
      // Keep the central ridge well clear of the finish so the final centre coin
      // (and the run-out) stay easy to reach.
      if (branch.start > 0.74) continue;
      mounds.push({ tStart: branch.start, tEnd: branch.end });
    }
  }

  // --- Extra open cliffs (one-sided), away from the chasm/bridges --------
  const maxCliffs = irng(cfg.cliffCount[0], cfg.cliffCount[1]);
  for (let i = 0; i < maxCliffs * 4 && cliffBands.length < bridgeBands.length + maxCliffs; i++) {
    const start = rng(0.15, 0.84);
    const band: Band = { start, end: start + rng(0.05, 0.08) };
    if (!clearOfChasm(band, 0.05)) continue;
    if (cliffBands.some((b) => overlaps(b, band, 0.06))) continue;
    // Don't open a cliff on the side the high lane occupies (it owns that edge).
    const sides: ('left' | 'right')[] = cfg.shelf
      ? [cfg.shelf.side > 0 ? 'left' : 'right']
      : ['left', 'right'];
    openEdges.push({ side: pick(sides), tStart: band.start, tEnd: band.end });
    cliffBands.push(band);
  }

  // --- Ramps (ski jumps) + extra bounce pads -----------------------------
  const ramps: RampPlacement[] = [];
  const rampPts: { t: number; lateral: number }[] = [];
  const maxRamps = irng(cfg.rampCount[0], cfg.rampCount[1]);
  const nearBridge = (t: number): boolean => bridgeBands.some((b) => t > b.start - 0.04 && t < b.end + 0.04);
  const onBridgeTight = (t: number): boolean => bridgeBands.some((b) => t > b.start - 0.018 && t < b.end + 0.018);
  const onFork = (t: number): boolean => !!cfg.shelf && cfg.shelf.forks.some((f) => t > f.tStart - 0.03 && t < f.tEnd + 0.03);
  // With a high lane, ski jumps sit on the fast line right at each launch ledge
  // (just before a fork), and grow toward the finish so the send gets bigger.
  const rampLat = cfg.shelf ? cfg.shelf.side * 0.5 : 0;
  if (cfg.shelf) {
    cfg.shelf.forks.forEach((f, i) => {
      ramps.push({ t: f.tStart - 0.02, lateral: rampLat, length: 0.045, rise: 2.8 + i * 1.1, width: 0.7 });
      rampPts.push({ t: f.tStart - 0.02, lateral: rampLat });
    });
  }
  // Scattered centre jumps fill the rest of the course (outside any high-lane
  // segment, which already has its own launch lips). On a pure-shelf course
  // like Frozen Rush the shelf spans nearly everything, so these all skip and
  // only the lips remain; on Icy Bridges the small shelf leaves room for jumps.
  const shelfLips = ramps.length;
  for (let i = 0; i < maxRamps * 8 && ramps.length < shelfLips + maxRamps; i++) {
    const t = rng(0.16, 0.86);
    if (gapBand && Math.abs(t - gapBand.start) < 0.1) continue;
    if (onBridgeTight(t) || onFork(t)) continue;
    if (shelfBand && t > shelfBand.start && t < shelfBand.end) continue;
    if (rampPts.some((rt) => Math.abs(rt.t - t) < 0.07)) continue;
    ramps.push({ t, lateral: 0, length: rng(0.03, 0.05), rise: rng(2.6, 4.2), width: 0.6 });
    rampPts.push({ t, lateral: 0 });
  }
  for (let i = 0; i < irng(1, 2); i++) {
    const t = rng(0.18, 0.86);
    if ((gapBand && Math.abs(t - gapBand.start) < 0.08) || nearBridge(t)) continue;
    bouncePads.push({ t, lateral: rng(-0.4, 0.4) });
  }

  const inChasm = (t: number): boolean => !!gapBand && t > gapBand.start - 0.02 && t < gapBand.end + 0.02;
  const onBridgeT = (t: number): boolean => bridgeBands.some((b) => t >= b.start - 0.01 && t <= b.end + 0.01);

  // --- Collectibles ------------------------------------------------------
  const collectibles: CollectiblePlacement[] = [];
  const fruitCount = irng(50, 70);
  const weaveFreq = rng(7, 12);
  const weavePhase = rng(0, Math.PI * 2);
  for (let i = 0; i < fruitCount; i++) {
    const t = 0.04 + (i / fruitCount) * 0.92;
    if (inChasm(t)) continue;
    let lateral: number;
    if (cfg.shelf && t > cfg.shelf.tStart) {
      // The ideal line runs down the centre of the fast high lane; at forks it
      // eases toward the middle so you can hop back on if you fell off.
      const atFork = cfg.shelf.forks.some((f) => t >= f.tStart && t <= f.tEnd);
      lateral = atFork ? cfg.shelf.side * 0.15 : cfg.shelf.side * (0.45 + 0.08 * Math.sin(t * weaveFreq + weavePhase));
    } else if (cfg.hurdleLine) {
      lateral = hlAt(t); // the fruit line runs right through the hurdles
    } else {
      // Keep the line near centre on narrow bridges so it doesn't lure you off.
      const amp = onBridgeT(t) ? 0.2 : 0.55;
      lateral = amp * Math.sin(t * weaveFreq + weavePhase);
    }
    collectibles.push({ t, lateral, tier: 'fruit' });
  }
  // Mound-fork courses get a light early snack to draw you in; the rest of the
  // opening density comes from the normal fruit line, kept on par with Bamboo
  // Descent rather than piled higher. (Coins/envelopes are reserved for the
  // earned spots — see the ridges below — not handed out at the easy start.)
  if (cfg.moundForks) {
    collectibles.push({ t: 0.16, lateral: 0, tier: 'food' });
  }
  // Each central-hill fork: a standout HIGH-reward perched on top of the ridge
  // (it auto-sits on the peak via surfaceHeight, so it's only reachable by
  // charging straight over — the first ridge is worth the most). The two easy
  // side-lanes get a fruit-and-dumpling mix only — no coins/envelopes, since the
  // ridge is where the risk/reward lives.
  for (let mi = 0; mi < mounds.length; mi++) {
    const m = mounds[mi];
    const mid = (m.tStart + m.tEnd) / 2;
    collectibles.push({ t: mid, lateral: 0, tier: mi === 0 ? 'envelope' : 'coin' });
    for (const side of [-1, 1] as const) {
      for (let i = 0; i < 3; i++) {
        const t = m.tStart + 0.025 + (i / 3) * (m.tEnd - m.tStart - 0.05);
        collectibles.push({ t, lateral: side * 0.72, tier: 'fruit' });
      }
      collectibles.push({ t: mid, lateral: side * 0.74, tier: 'food' });
    }
  }
  // A sparse trail on the slow low lane too, so it's not empty — just far less
  // rewarding than committing to the high line. Spans the shelf's own range
  // (so a short shelf segment doesn't fling collectibles past the finish).
  if (cfg.shelf) {
    const sEnd = cfg.shelf.tEnd ?? 0.86;
    const span = sEnd - cfg.shelf.tStart;
    const n = Math.max(2, Math.round(span / 0.08));
    for (let i = 0; i < n; i++) {
      const t = cfg.shelf.tStart + 0.04 + (i / n) * Math.max(0, span - 0.08);
      collectibles.push({ t, lateral: -cfg.shelf.side * rng(0.4, 0.6), tier: 'fruit' });
    }
  }
  for (let i = 0; i < irng(7, 11); i++) {
    const t = rng(0.1, 0.9);
    if (inChasm(t) || onBridgeT(t)) continue;
    let lat: number;
    if (cfg.shelf && t > cfg.shelf.tStart) {
      lat = cfg.shelf.side * rng(0.5, 0.8); // riches reward staying on the high lane
    } else if (cfg.hurdleLine) {
      lat = -Math.sign(hlAt(t) || 1) * rng(0.55, 0.8); // food on the clean line
    } else {
      lat = (chance(0.5) ? 1 : -1) * rng(0.55, 0.82);
    }
    collectibles.push({ t, lateral: lat, tier: 'food' });
  }
  // Coins ride the ramp jumps (earned/risk) — but NOT the first ramp, which
  // gets the jackpot envelope instead, so the two never stack on one spot.
  rampPts.forEach((rp, i) => {
    if (i === 0) return;
    collectibles.push({ t: rp.t, lateral: rp.lateral, tier: 'coin', height: 2.3 });
  });
  // Branch (widened-section) coins only on courses WITHOUT central-hill forks —
  // there the ridge tops already own the coin/envelope rewards.
  if (!cfg.moundForks) {
    for (const branch of branches)
      collectibles.push({ t: (branch.start + branch.end) / 2, lateral: rng(0.6, 0.82), tier: 'coin' });
  }
  if (rampPts.length > 0) {
    collectibles.push({ t: rampPts[0].t, lateral: rampPts[0].lateral, tier: 'envelope', height: 2.8 });
  } else if (branches.length > 0 && !cfg.moundForks) {
    collectibles.push({ t: (branches[0].start + branches[0].end) / 2, lateral: -0.78, tier: 'envelope' });
  }
  // A little gauntlet just before the finish so the end isn't a quiet coast: a
  // jump over a centre coin, flanked by a snack and a closing envelope.
  if (cfg.endFlourish) {
    ramps.push({ t: 0.88, lateral: 0, length: 0.04, rise: 3.2, width: 0.6 });
    collectibles.push({ t: 0.88, lateral: 0, tier: 'coin', height: 2.4 });
    collectibles.push({ t: 0.85, lateral: 0.55, tier: 'food' });
    collectibles.push({ t: 0.92, lateral: 0, tier: 'envelope', height: 1.2 });
  }
  // A short fruit run-in (with a dumpling) guiding you over the otherwise flat,
  // empty last stretch into the finish-line coin.
  for (let i = 0; i < 6; i++) {
    const t = 0.925 + (i / 6) * 0.06; // ~0.925 .. ~0.985
    collectibles.push({ t, lateral: 0.3 * Math.sin(i * 1.6 + weavePhase), tier: 'fruit' });
  }
  collectibles.push({ t: 0.955, lateral: -0.35, tier: 'food' });
  const safeCollectibles = collectibles.filter((c) => !inChasm(c.t));

  // --- Obstacles ---------------------------------------------------------
  const obstacles: ObstaclePlacement[] = [];
  const obsTs: number[] = [];
  const maxObstacles = irng(cfg.obstacleCount[0], cfg.obstacleCount[1]);
  for (let i = 0; i < maxObstacles * 4 && obstacles.length < maxObstacles; i++) {
    const t = rng(0.1, 0.92);
    if (approachBand && overlaps({ start: t, end: t }, approachBand, 0.01)) continue;
    if (onBridgeT(t)) continue; // keep bridges clear
    if (obsTs.some((ot) => Math.abs(ot - t) < 0.045)) continue;
    // On a high-lane course, keep obstacles on the low (slow) side so the fast
    // line stays clean.
    let lateral: number;
    if (cfg.shelf) lateral = -cfg.shelf.side * rng(0.1, 0.7);
    else if (cfg.hurdleLine) lateral = hlAt(t);
    else lateral = rng(-cfg.obstacleSpread, cfg.obstacleSpread);
    obstacles.push({ t, lateral, type: pick(cfg.obstacleTypes) });
    obsTs.push(t);
  }

  // Mound-fork courses get a few near-centre obstacles in the opening run to
  // weave around, so the start has something to do before the first fork.
  if (cfg.moundForks) {
    const opener: ObstaclePlacement[] = [
      { t: 0.08, lateral: 0.0, type: 'rock' },
      { t: 0.135, lateral: -0.32, type: 'log' },
      { t: 0.17, lateral: 0.34, type: 'rock' },
      { t: 0.225, lateral: 0.0, type: 'log' },
      { t: 0.27, lateral: -0.3, type: 'rock' },
    ];
    obstacles.push(...opener);
  }

  // Give each fork's two side-lanes an obstacle as well, so committing to a path
  // means weaving, not just coasting past the ridge.
  for (const m of mounds) {
    obstacles.push({ t: m.tStart + 0.03, lateral: -0.72, type: 'rock' });
    obstacles.push({ t: m.tEnd - 0.03, lateral: 0.72, type: 'log' });
  }

  // Drop one obstacle in the lull between consecutive bridges, alternating
  // sides, so crossing the bridges means actively steering — not just holding a
  // straight line from one to the next.
  if (bridgeBands.length >= 2) {
    const sorted = [...bridgeBands].sort((a, b) => a.start - b.start);
    let side = 1;
    for (let i = 0; i < sorted.length - 1; i++) {
      const midT = (sorted[i].end + sorted[i + 1].start) / 2;
      if (sorted[i + 1].start - sorted[i].end < 0.06) continue; // too tight to fit
      if (shelfBand && overlaps({ start: midT, end: midT }, shelfBand, 0.02)) continue;
      obstacles.push({ t: midT, lateral: side * 0.42, type: pick(cfg.obstacleTypes) });
      side *= -1;
    }
  }

  // Closing gauntlet near the finish (paired with the end-flourish collectibles).
  if (cfg.endFlourish) {
    obstacles.push(
      { t: 0.83, lateral: -0.45, type: 'rock' },
      { t: 0.86, lateral: 0.45, type: 'log' },
      { t: 0.905, lateral: -0.28, type: 'rock' },
    );
  }

  const parTime = Math.max(90, Math.min(280, Math.round((descentLen / 13) * 1.5)));

  return {
    id: cfg.id,
    name: cfg.name,
    parTime,
    controlPoints,
    halfWidths,
    banks,
    bankHeight: 9,
    theme: cfg.theme,
    finishControlIndex: DESC,
    openEdges,
    gaps,
    surfaceZones,
    ramps,
    bouncePads,
    collectibles: safeCollectibles,
    obstacles,
    mounds: mounds.length ? mounds : undefined,
    shelf: cfg.shelf ?? undefined,
    checkpointCount: Math.max(8, Math.round(DESC / 3)),
  };
}

// ---- The six courses (#3 is the hand-tuned Bamboo Descent) --------------
// Palettes draw only from the three surfaces. grippy = the slow option (mud),
// slippery = the fast option (ice); grass is the untouched default everywhere.
const GRIPPY: SurfaceId[] = ['mud'];
const SLIPPERY: SurfaceId[] = ['ice'];

const base = {
  hurdleLine: false,
  iceBase: false,
  laneLow: null as SurfaceId | null,
  moundForks: false,
  shelf: null as ShelfConfig | null,
  endFlourish: false,
  benches: 0, // smooth ramp by default; mountain courses override
  theme: 'green' as 'green' | 'snow',
};

const GENTLE_MEADOW = buildLevel({
  ...base,
  id: 'gentle-meadow',
  name: 'Gentle Meadow',
  seed: 101,
  desc: 34,
  ampA: 9,
  freqA: 1.7,
  ampB: 3,
  freqB: 4,
  baseHW: 9.5, // wide
  branches: 3, // different tracks down
  grippy: GRIPPY,
  slippery: SLIPPERY,
  patchCount: [2, 3],
  iceBias: 0.25,
  cliffCount: [0, 1],
  rampCount: [1, 2],
  obstacleCount: [3, 5],
  obstacleTypes: ['rock', 'log'],
  obstacleSpread: 0.5,
  gap: false,
  bridges: 0,
  gradeBase: 0.09,
  gradeSteep: 0.22,
});

const HURDLE_RUN = buildLevel({
  ...base,
  id: 'hurdle-run',
  name: 'Hurdle Run',
  seed: 202,
  desc: 36,
  ampA: 7,
  freqA: 1.5, // straight
  ampB: 4,
  freqB: 5,
  baseHW: 7,
  branches: 1,
  grippy: GRIPPY,
  slippery: SLIPPERY,
  patchCount: [3, 4],
  iceBias: 0.15,
  cliffCount: [0, 1],
  rampCount: [3, 4],
  obstacleCount: [12, 15], // lots of hurdles, on the reward line
  obstacleTypes: ['fence', 'fence', 'log', 'rock'], // a few rocks to mix it up
  obstacleSpread: 0.25,
  hurdleLine: true, // fruit line runs through the hurdles
  endFlourish: true, // a final jump + rocks so the finish isn't a flat coast
  gap: false,
  bridges: 0,
  gradeBase: 0.1,
  gradeSteep: 0.28,
});

const FROZEN_RUSH = buildLevel({
  ...base,
  id: 'frozen-rush',
  name: 'Frozen Rush',
  seed: 404,
  desc: 38,
  ampA: 16,
  freqA: 2.4,
  ampB: 8,
  freqB: 6,
  baseHW: 9.5, // wide, so there's room for a high & low line
  branches: 0,
  grippy: GRIPPY,
  slippery: SLIPPERY,
  patchCount: [0, 1], // ice base covers most of it
  iceBias: 1,
  cliffCount: [0, 1],
  rampCount: [3, 4], // ski jumps up on the fast line
  obstacleCount: [3, 5],
  obstacleTypes: ['rock', 'log'],
  obstacleSpread: 0.4,
  iceBase: true, // frozen the whole way — both lanes are ice
  // The high lane is the fast, rewarding line. It climbs gently then launches
  // off a ledge into each wide fork gap (the gaps give room to swap lanes);
  // slip toward centre and you drop to the low lane until the next gap.
  shelf: {
    side: 1,
    height: 6,
    innerLN: 0.05,
    tStart: 0.12,
    forks: [
      { tStart: 0.26, tEnd: 0.37 },
      { tStart: 0.49, tEnd: 0.6 },
      { tStart: 0.71, tEnd: 0.82 },
    ],
  },
  gap: false, // ski jumps instead of a chasm (keeps clear of the high lane)
  bridges: 0,
  gradeBase: 0.14,
  gradeSteep: 0.46, // steep & fast
  benches: 4, // alternating steep pitches & benches read as a mountain face
  theme: 'snow',
});

const WINDING_WAY = buildLevel({
  ...base,
  id: 'winding-way',
  name: 'Winding Way',
  seed: 505,
  desc: 52, // long enough for the tight curves, short enough to stay lively
  ampA: 27,
  freqA: 5.0, // roughly double Bamboo Descent's curvature, busy from the start
  ampB: 14,
  freqB: 10,
  baseHW: 8,
  branches: 3, // widened sections become central-hill forks
  grippy: GRIPPY,
  slippery: SLIPPERY,
  patchCount: [3, 5],
  iceBias: 0.55, // lean toward the fun fast ice over slow mud
  cliffCount: [1, 2],
  rampCount: [3, 4],
  obstacleCount: [7, 10],
  obstacleTypes: ['rock', 'log', 'fence'],
  obstacleSpread: 0.45,
  moundForks: true, // tall central hills split the path left/right
  endFlourish: true, // a closing jump + rocks so the run-in has a finale
  // No chasm: the curves + central-hill forks + ramps are the challenge, and a
  // chasm on so curvy a line tends to fling you off on the landing.
  gap: false,
  bridges: 0,
  gradeBase: 0.22, // steep & quick right out of the gate so the start has pace
  gradeSteep: 0.16,
  benches: 5, // stepped mountain descent: steep drops between flatter shelves
  theme: 'snow',
});

const ICY_BRIDGES = buildLevel({
  ...base,
  id: 'icy-bridges',
  name: 'Icy Bridges',
  seed: 606,
  desc: 46,
  ampA: 26,
  freqA: 4.6, // windy
  ampB: 12,
  freqB: 8,
  baseHW: 7,
  branches: 1, // the opening central-hill fork (see moundForks)
  grippy: GRIPPY,
  slippery: SLIPPERY,
  patchCount: [10, 12],
  iceBias: 0.95,
  cliffCount: [1, 2],
  rampCount: [3, 4], // jumps scattered through
  obstacleCount: [5, 8],
  obstacleTypes: ['rock', 'log', 'fence'],
  obstacleSpread: 0.35,
  // The finale course: it folds in elements from the earlier ones — an opening
  // fork/ridge (Winding Way), a high/low lane segment (Frozen Rush), jumps, and
  // obstacles tucked between the bridges so crossing them takes real steering.
  moundForks: true,
  shelf: {
    side: 1,
    height: 5,
    innerLN: 0.05,
    tStart: 0.6,
    tEnd: 0.72,
    forks: [{ tStart: 0.655, tEnd: 0.7 }],
  },
  endFlourish: true,
  gap: false,
  bridges: 12, // many candidates; the fork & shelf zones thin them out
  gradeBase: 0.11,
  gradeSteep: 0.34,
  benches: 5, // mountain face: steep pitches alternating with flatter benches
  theme: 'snow',
});

/** The six selectable courses, in order. */
export const LEVELS: LevelData[] = [
  GENTLE_MEADOW,
  HURDLE_RUN,
  LEVEL_1, // Bamboo Descent — the original, hand-tuned
  FROZEN_RUSH,
  WINDING_WAY,
  ICY_BRIDGES,
];
